import React, { useState, useEffect, useRef } from 'react';

// Types
import { UpdateAiTemplateSectionType } from 'Shared/types/UpdateAiTemplate.type';

// Hooks
import { useUpdateAiReport } from 'Shared/utils/hooks/use-update-ai-report';
import { useUpdateAiSetting } from 'Shared/utils/hooks/use-update-ai-setting';

// Components
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpCollapsible } from 'Wpds/components/collapsible';
import DefaultTemplateSectionListItem from './DefaultTemplateSectionListItem';

// Styles
import './DefaultTemplateSectionList.scss';

type Props = {
  updateAiSettingSlug: string;
};

const DefaultTemplateSectionList = ({ updateAiSettingSlug }: Props) => {
  const scrollToRef = useRef(null);

  const { defaultTemplate, report } = useUpdateAiSetting({
    slug: updateAiSettingSlug,
  });
  const defaultTemplateSections = defaultTemplate?.updateAiTemplateSections || [];
  const { isGenerating, hasGeneratedSections } = useUpdateAiReport({
    slug: report?.slug,
  });

  const [openCollapsible, setOpenCollapsible] = useState(true);

  const blankSection: UpdateAiTemplateSectionType = {
    title: 'Blank',
    iconName: 'pencil',
    description: 'Create your own prompt. Be sure to be specific.',
    slug: 'blank',
    position: -1,
    active: false,
  };

  const toggleOpenCollapsible = () => {
    setOpenCollapsible(!openCollapsible);
    if (!openCollapsible) {
      setTimeout(() => scrollToRef.current?.scrollIntoView({ behavior: 'smooth' }), 400);
    }
  };

  useEffect(() => {
    if (isGenerating) {
      setOpenCollapsible(false);
    }
  }, [isGenerating]);

  useEffect(() => {
    if (hasGeneratedSections) {
      setOpenCollapsible(false);
    }
  }, []);

  if (isGenerating) return null;

  const baseClassName = 'DefaultTemplateSectionList';
  return (
    <div className={baseClassName}>
      {hasGeneratedSections ? (
        <div className={`${baseClassName}__button`}>
          <WpButton onClick={toggleOpenCollapsible} color="gray" block>
            <WpIcon name={openCollapsible ? 'angle-up' : 'plus-circle'} />
            {openCollapsible ? 'Hide' : 'Add Section'}
          </WpButton>
        </div>
      ) : (
        <p className={`${baseClassName}__label`}>Add Section:</p>
      )}
      <WpCollapsible open={openCollapsible} animationDuration={400}>
        <div className={`${baseClassName}__list`}>
          {[...defaultTemplateSections, blankSection].map(section => (
            <DefaultTemplateSectionListItem
              key={section.slug}
              updateAiSettingSlug={updateAiSettingSlug}
              section={section}
            />
          ))}
        </div>
      </WpCollapsible>
      <div ref={scrollToRef} />
    </div>
  );
};

export default DefaultTemplateSectionList;
